/**
 * CollapseSkillEffect — Astral dense-path evolution: targeted gravity root.
 *
 * Replaces Dense at XP 1200. Inherits all Dense effects (knockback immunity,
 * proximity stagger, gravity wake on Phase Step, 30% movement speed reduction)
 * and adds a targeting component:
 *
 *   With a target: designate the nearest enemy within range. That enemy receives
 *   a 2.5s movement-only root and a damage field spawns at their position —
 *   nearby enemies (excluding the root target) take 5 dmg/sec.
 *
 *   Without a target: Dense effects still apply to the player. The enemy-targeting
 *   component simply doesn't fire.
 *
 * Root is movement-only CC — prevents movement but not attacks or abilities.
 * No immunity window. Coexists with all other CC types (ADR-065).
 *
 * The root timer (2500ms) is independent of the Collapse buff timer (5000–7000ms).
 * The damage zone lasts for the root duration at the target's fixed position.
 *
 * Upgrade hooks (inherited from Dense, scoped to 'dense'):
 * - dense_knockback (Mass Differential): contactKnockbackTiles
 * - dense_duration (Sustained Mass): durationMs, gravityWakeDurationMs
 *
 * Phase sequence: active window (5000ms, extendable) → medium cooldown (1200ms).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § collapse
 * @see design-decisions.md § Astral Base Values (damage rate 5/sec)
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 */

import { DenseSkillEffect, type DenseDeps, type DenseConfig } from './DenseSkillEffect.js';
import type { GravityPullSystem } from '../../../systems/GravityPullSystem.js';

// ---------------------------------------------------------------------------
// Dependency extensions
// ---------------------------------------------------------------------------

export interface CollapseDeps extends DenseDeps {
  /** Query enemies by proximity for targeting and damage field ticking. */
  readonly enemies: {
    getEnemiesInRadius(x: number, y: number, radiusTiles: number): string[];
    getNearestEnemy(radiusTiles: number): string | null;
    getPosition(enemyId: string): { x: number; y: number } | null;
  };
  /** Deal damage to enemies caught in the collapse damage field. */
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
  /** Optional gravity pull for visual/gameplay pull toward root center. */
  readonly gravityPull?: Pick<GravityPullSystem, 'addWell' | 'removeWell'>;
}

// ---------------------------------------------------------------------------
// Config extensions
// ---------------------------------------------------------------------------

export interface CollapseConfig extends DenseConfig {
  /** Root duration applied to the targeted enemy in ms (2500). */
  readonly rootDurationMs: number;
  /** Damage dealt to each enemy per tick (5 at 1s intervals = 5/sec). */
  readonly damagePerTick: number;
  /** Interval between damage ticks in ms (1000). */
  readonly damageTickIntervalMs: number;
  /** Radius of the damage field around the rooted target in tiles (1.5). */
  readonly damageFieldRadiusTiles: number;
  /** Maximum range to acquire a target on activation in tiles (3). */
  readonly targetingRangeTiles: number;
}

const COLLAPSE_DEFAULTS: Omit<CollapseConfig, keyof DenseConfig> = {
  rootDurationMs: 2500,
  damagePerTick: 5,
  damageTickIntervalMs: 1000,
  damageFieldRadiusTiles: 1.5,
  targetingRangeTiles: 3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CollapseSkillEffect extends DenseSkillEffect {
  override readonly skillId = 'collapse';

  private readonly collapseDeps: CollapseDeps;
  readonly collapseConfig: CollapseConfig;

  /** ID of the currently rooted enemy, or null if no target was acquired. */
  private _rootedEnemyId: string | null = null;

  /** Fixed position of the rooted enemy (captured at activation). */
  private _rootedPosition: { x: number; y: number } | null = null;

  /** Remaining ms on the root applied to the target. */
  private _rootTimeRemaining = 0;

  /** Accumulator for damage tick timing. */
  private _damageAccumulator = 0;

  /** Running zone ID counter for damage field zones. */
  private _fieldIdCounter = 0;

  /** Active gravity well ID for cleanup. */
  private _wellId: string | null = null;

  // --- Telemetry ---

  private _totalDamageDealt = 0;
  private _damageFieldTicks = 0;
  private _rootsApplied = 0;
  private _fieldsCreated = 0;

  constructor(deps: CollapseDeps, config: Partial<CollapseConfig> = {}) {
    super(deps, config);
    this.collapseDeps = deps;
    this.collapseConfig = {
      ...(this.config as DenseConfig),
      ...COLLAPSE_DEFAULTS,
      ...config,
    } as CollapseConfig;
  }

  // --- State getters ---

  get rootedEnemyId(): string | null { return this._rootedEnemyId; }
  get rootedPosition(): { x: number; y: number } | null { return this._rootedPosition; }
  get rootTimeRemaining(): number { return this._rootTimeRemaining; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get damageFieldTicks(): number { return this._damageFieldTicks; }
  get rootsApplied(): number { return this._rootsApplied; }
  get fieldsCreated(): number { return this._fieldsCreated; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect overrides
  // ---------------------------------------------------------------------------

  override activate(): boolean {
    // Dense activation handles buff, knockback immunity, speed reduction.
    const activated = super.activate();
    if (!activated) return false;

    // Attempt to acquire a target for the Collapse component.
    const targetId = this.collapseDeps.enemies.getNearestEnemy(
      this.collapseConfig.targetingRangeTiles,
    );

    if (targetId) {
      const pos = this.collapseDeps.enemies.getPosition(targetId);
      if (pos) {
        this._rootedEnemyId = targetId;
        this._rootedPosition = { x: pos.x, y: pos.y };
        this._rootTimeRemaining = this.collapseConfig.rootDurationMs;
        this._damageAccumulator = 0;
        this._rootsApplied++;

        // Apply movement-only root to the target (ADR-065: no immunity window).
        this.deps.statusEffects.apply(
          'root',
          this.collapseConfig.rootDurationMs,
          'collapse',
        );

        // Spawn visual damage zone at the target's position.
        this._fieldsCreated++;
        this.deps.zones.create({
          id: `collapse_field_${this._fieldIdCounter++}`,
          type: 'collapse_damage_field',
          position: { x: pos.x, y: pos.y },
          durationMs: this.collapseConfig.rootDurationMs,
          sourceSkillId: 'collapse',
          shape: 'circle',
          radiusTiles: this.collapseConfig.damageFieldRadiusTiles,
        });

        // Register gravity well for visual pull toward root center.
        if (this.collapseDeps.gravityPull) {
          const wellId = `collapse_well_${this._fieldIdCounter}`;
          this.collapseDeps.gravityPull.addWell({
            id: wellId,
            position: { x: pos.x, y: pos.y },
            pullRadiusTiles: this.collapseConfig.damageFieldRadiusTiles,
            pullStrengthTilesPerSec: 1.0,
            durationMs: this.collapseConfig.rootDurationMs,
            sourceSkillId: 'collapse',
          });
          this._wellId = wellId;
        }
      }
    }

    return true;
  }

  protected override updateWhileActive(delta: number): void {
    // Tick root timer and damage field before Dense window check.
    if (this._rootedEnemyId && this._rootTimeRemaining > 0) {
      this._rootTimeRemaining -= delta;

      if (this._rootTimeRemaining <= 0) {
        // Root expired — damage field stops. Position retained for inspection.
        this._rootTimeRemaining = 0;
        this._rootedEnemyId = null;
      } else {
        this._tickDamageField(delta);
      }
    }

    // Dense handles its own window countdown and auto-deactivation.
    super.updateWhileActive(delta);
  }

  protected override onDeactivate(): void {
    this._clearTargeting();
    super.onDeactivate();
  }

  override destroy(): void {
    this._clearTargeting();
    super.destroy();
  }

  // ---------------------------------------------------------------------------
  // Damage field
  // ---------------------------------------------------------------------------

  private _tickDamageField(delta: number): void {
    if (!this._rootedPosition) return;

    this._damageAccumulator += delta;
    const interval = this.collapseConfig.damageTickIntervalMs;

    while (this._damageAccumulator >= interval) {
      this._damageAccumulator -= interval;

      const enemies = this.collapseDeps.enemies.getEnemiesInRadius(
        this._rootedPosition.x,
        this._rootedPosition.y,
        this.collapseConfig.damageFieldRadiusTiles,
      );

      for (const enemyId of enemies) {
        // Exclude the rooted target — they're the hazard, not a victim.
        if (enemyId !== this._rootedEnemyId) {
          this.collapseDeps.combat.dealDamage(
            enemyId,
            this.collapseConfig.damagePerTick,
          );
          this._totalDamageDealt += this.collapseConfig.damagePerTick;
        }
      }

      this._damageFieldTicks++;
    }
  }

  private _clearTargeting(): void {
    if (this._wellId && this.collapseDeps.gravityPull) {
      this.collapseDeps.gravityPull.removeWell(this._wellId);
      this._wellId = null;
    }
    this._rootedEnemyId = null;
    this._rootedPosition = null;
    this._rootTimeRemaining = 0;
    this._damageAccumulator = 0;
  }
}
